import './styles/Confirm.scss';

import { useMutation } from '@apollo/client';
import { Grid, Button, TextField } from '@mui/material';

import { Formik, Field } from 'formik';
import { Gateway } from 'index';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import SET_PASSWORD_MUTATION from 'gql/mutations/setPassword.gql';

import Loader from 'app/components/Util/Loader';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import ErrorMarker from './components/ErrorMarker';

export default function Confirm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const { token } = useParams<{ token: string }>();
  const [setPassword] = useMutation(SET_PASSWORD_MUTATION);

  useEffect(() => {
    const checkToken = async () => {
      await Gateway.get(`/users/access/${token}`).catch(() => setIsValid(false));
      setIsValid(true);
    };
    checkToken();
  }, [token]);

  if (isValid === null) {
    return <Loader />;
  }

  if (isValid === false) {
    dispatch(AlertActions.error(t(translations.pages.login.alerts.confirmFailed())));
    navigate('/login', { replace: true });
  }

  const validationSchema = {
    newPassword: yup
      .string()
      // Minimun 8 characters, min 1 digit, min 1 upper case, min 1 special char
      .matches(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/g,
        t(translations.pages.login.passwordHelperText())
      )
      .required(t(translations.validation.required())),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('newPassword')], t(translations.validation.passwordMatch()))
      .required()
  };

  const handleSetPassword = async values => {
    try {
      await setPassword({
        variables: {
          token,
          password: values.newPassword
        }
      });
      dispatch(AlertActions.success(t(translations.pages.login.alerts.setPasswordSuccessful())));
      navigate('/login');
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.login.alerts.setPasswordFailed())));
    }
  };

  return (
    <>
      <Helmet>
        <title>{t(translations.pages.login.confirmTitle())}</title>
      </Helmet>
      <Formik
        onSubmit={values => handleSetPassword(values)}
        initialValues={{
          newPassword: '',
          confirmPassword: ''
        }}
        validationSchema={yup.object().shape(validationSchema)}
        isInitialValid={false}
      >
        {({ handleSubmit, touched, errors, isValid }) => (
          <form onSubmit={handleSubmit}>
            <Grid container className="confirm-form">
              <Grid container>
                <Grid container className="confirm-form__title">
                  {t(translations.pages.login.confirmTitle())}
                </Grid>
                <Grid container className="confirm-form__description">
                  {t(translations.pages.login.confirmDescription())}
                </Grid>
              </Grid>
              <Grid container>
                <Grid container>
                  <Grid item xs={11} md>
                    <Field
                      className="confirm-form__field"
                      label={t(translations.common.password())}
                      name="newPassword"
                      as={TextField}
                      error={touched.newPassword && errors.newPassword}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="password"
                      helperText={t(translations.pages.login.passwordHelperText())}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={11} md>
                    <Field
                      className="confirm-form__field"
                      label={t(translations.common.confirmPassword())}
                      name="confirmPassword"
                      as={TextField}
                      error={touched.confirmPassword && errors.confirmPassword}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="password"
                    />
                  </Grid>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <ErrorMarker message={errors.confirmPassword} />
                  )}
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end">
                <Button color="primary" variant="contained" disabled={!isValid} type="submit">
                  {t(translations.common.confirm())}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </>
  );
}
